<template>
  <div class="applicant-import-dialog">
    <el-dialog
      custom-class="with-title"
      :title="typeText + 'インポート'"
      :visible="visible"
      :show-close="!loading"
      @close="hide"
    >
      <div
        v-loading="loading"
        :element-loading-text="typeText + 'インポートを実行しています、しばらくお待ちください。'"
      >
        <div class="dialog-content">
          <div class="message message-info mb-x3">
            <div>{{ typeText + 'の@ではじまるTikTokユーザー名を入力してください。' }}</div>
            <div>
              {{ '入力したユーザーが応募者に含まれる場合、' + typeText + 'フラグがONになります。' }}
            </div>
            <div>複数入力する場合、1アカウント1行となるよう改行してください。</div>
          </div>
          <div class="input-container mb-x3">
            <el-input
              class="body-textarea"
              v-model="body"
              id="text"
              type="textarea"
              :class="{ error: $v.body.required && $v.body.$error, success: $v.body.required && !$v.body.invalid }"
              placeholder="TikTokユーザー名を入力"
              @blur="$v.body.$touch"
              :rows="10"
            ></el-input>
            <div class="input-invalid-text" v-if="!$v.body.required && $v.body.$error">
              TikTokユーザー名を入力してください。
            </div>
            <div class="input-invalid-text" v-else-if="maxUserError">
              一度にインポートできる件数（10,000件）を超えています。
            </div>
          </div>
        </div>
        <div class="dialog-footer" slot="footer">
          <div layout layout-align="end-center">
            <el-button type="primary" @click="submit()" :disabled="disabled"
              ><i class="mdi mdi-upload"></i>インポート実行</el-button
            >
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss">
@use 'sass:math';
@use '@/client/assets/scss/variables.scss' as *;

.applicant-import-dialog {
  .el-loading-spinner {
    margin-top: math.div(-$spacing-unit * 12, 2) - $spacing-unit * 2;

    .circular {
      width: $spacing-unit * 12;
      height: $spacing-unit * 12;
    }

    .el-loading-text {
      margin-top: $spacing-unit * 2;
      font-weight: bold;
      color: $text-color;
    }
  }

  .body-textarea {
    textarea {
      height: 260px;
    }
  }
}
</style>
<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import { required } from 'vuelidate/lib/validators'

import api from '@/client/core/api'
import { useTikTokApplicantStore } from '@/client/store/useTikTokApplicantStore'

export default defineComponent({
  data() {
    return {
      type: '',
      types: {
        follower: {
          text: 'フォロー'
        },
        winner: {
          text: '当選者'
        },
        candidate: {
          text: '候補者'
        },
        responder: {
          text: '回答者'
        },
        winner_contact: {
          text: '当選連絡'
        }
      },
      body: '',
      visible: false,
      loading: false,
      isSubmit: false
    }
  },
  computed: {
    ...mapState(useTikTokApplicantStore, {
      prizeId: state => state.campaignPrize?.id
    }),
    typeText() {
      if (!this.types[this.type]) {
        return ''
      }

      return this.types[this.type].text
    },
    isFormChanged() {
      return this.body.trim()
    },
    maxUserError() {
      if (!this.body.trim()) {
        return false
      }

      return this.body.trim().split('\n').length > 10000
    },
    disabled() {
      return this.$v.$invalid || !this.body.trim() || this.isSubmit || this.maxUserError
    }
  },
  methods: {
    ...mapActions(useTikTokApplicantStore, ['fetchApplicants', 'fetchCampaignPrize']),

    /**
     * ダイアログをリセット
     */
    reset() {
      this.loading = false
      this.isSubmit = false
    },

    /**
     * フォームのサブミット
     */
    async submit() {
      if (this.isSubmit) {
        return
      }

      const value = this.body
      const applicants = value
        .replace(/^\s*[\r\n]/gm, '')
        .replace(/^\s+|\s+$/g, '')
        .replace(/ /g, '')
        .replace(/@/g, '')
        .split('\n')

      const confirm = window.confirm(
        `${applicants.length}件のレコードをインポートします。\n処理を実行してよろしいですか?\nこの操作は取り消しできません。`
      )

      if (!confirm) {
        return
      }
      this.isSubmit = true
      this.loading = true

      const params = {
        prizeId: this.prizeId,
        applicants,
        type: this.type
      }

      const result = await api.post('/tiktok_applicants/imports', params)

      this.loading = false
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      // 残り当選者数が足りない場合はエラーを表示
      if (result && result.error && result.error.type === 'REMAIN_WINNER_OVER') {
        this.$notify({
          title: '残り当選者数が不足しています。',
          message: 'インポートする当選者数を確認してください。',
          customClass: 'danger',
          duration: 5000
        })
        this.fetchCampaignPrize()
        this.fetchApplicants()
        return
      }

      // 失敗した場合はエラーを表示
      if (result.data === undefined) {
        this.$notify({
          title: `${this.typeText}インポートに失敗しました。`,
          message: '恐れ入りますが、時間を置いて再度お試しください。',
          customClass: 'danger',
          duration: 5000
        })
        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: `${result.data}件の${this.typeText}をインポートしました。`,
        message: '',
        customClass: 'success',
        duration: 5000
      })

      this.fetchCampaignPrize()
      this.fetchApplicants()
      this.close()
      this.body = ''
    },

    /**
     * ダイアログを開く
     */
    open(type: string) {
      this.type = type
      this.body = ''
      this.$v.$reset()
      this.visible = true
      this.reset()
    },

    /**
     * ダイアログを閉じる
     */
    close() {
      this.visible = false
    },

    hide() {
      if (this.isFormChanged) {
        const message = '編集内容が保存されていません。ダイアログを閉じてよろしいですか?'
        const confirm = window.confirm(message)

        if (!confirm) {
          return
        }
      }

      this.visible = false
    }
  },
  validations: {
    body: {
      required
    }
  }
})
</script>
